/**
 * Copyright (c) 2026, CheckPlus Limited
 */
/**
 * Custom Textbox component to replace react-inputs-validation
 * Maintains the same API for backward compatibility
 */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import "./Textbox.css";

const Textbox = ({
  id,
  name,
  type = "text",
  placeholder,
  value,
  onChange,
  onBlur,
  classNameInput,
  classNameContainer,
  validationOption = {},
  validationCallback,
  validate,
  disabled,
  maxLength,
}) => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [touched, setTouched] = useState(false);

  const validateValue = useCallback((val) => {
    const { required, min, customFunc, msgOnError } = validationOption;

    // Check required
    if (required && (!val || val.trim() === "")) {
      return { isValid: false, message: msgOnError || "This field is required" };
    }

    // Check minimum length
    if (min && val && val.length < min) {
      return { isValid: false, message: msgOnError || `Minimum ${min} characters required` };
    }

    // Check custom validation function
    if (customFunc && typeof customFunc === "function") {
      const result = customFunc(val);
      if (result === false) {
        return { isValid: false, message: msgOnError || "Invalid input" };
      }
    }

    return { isValid: true, message: "" };
  }, [validationOption]);

  // Run validation when validate prop changes to true (external trigger)
  useEffect(() => {
    if (validate) {
      const { isValid, message } = validateValue(value);
      setHasError(!isValid);
      setErrorMessage(message);
      if (validationCallback) {
        validationCallback(!isValid);
      }
    }
  }, [validate, value, validateValue, validationCallback]);

  // Run validation on value change if touched
  useEffect(() => {
    if (touched) {
      const { isValid, message } = validateValue(value);
      setHasError(!isValid);
      setErrorMessage(message);
    }
  }, [value, touched, validateValue]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleBlur = (e) => {
    setTouched(true);
    const { isValid, message } = validateValue(value);
    setHasError(!isValid);
    setErrorMessage(message);

    if (validationCallback) {
      validationCallback(!isValid);
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <div className={classNameContainer || "textbox-container"}>
      <input
        id={id}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value || ""}
        onChange={handleChange}
        onBlur={handleBlur}
        className={`${classNameInput || "textbox-input"} ${hasError ? "textbox-error" : ""}`}
        disabled={disabled}
        maxLength={maxLength}
      />
      {hasError && errorMessage && (
        <div className="textbox-error-message">{errorMessage}</div>
      )}
    </div>
  );
};

Textbox.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  classNameInput: PropTypes.string,
  classNameContainer: PropTypes.string,
  validationOption: PropTypes.shape({
    name: PropTypes.string,
    check: PropTypes.bool,
    required: PropTypes.bool,
    min: PropTypes.number,
    customFunc: PropTypes.func,
    msgOnError: PropTypes.string,
  }),
  validationCallback: PropTypes.func,
  validate: PropTypes.bool,
  disabled: PropTypes.bool,
  maxLength: PropTypes.string,
};

export default Textbox;
